import { useEffect, useState } from 'react';
import { useTranslation } from '../../../i18n';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { getErrorMessage } from '../../../lib/errors';
import { useAuthStore } from '../../../stores/authStore';
import { CardNB, ButtonNB, IconNB, SelectNB } from '../../../components/ui-nb';
import { error as logError } from '../../../lib/logger';

type DateRange = 'this_week' | 'this_month' | 'custom';

interface Metrics {
  total_orders: number;
  revenue: number;
  avg_repair_time: number;
  top_equipment: { type: string; count: number }[];
}

interface StatusCount {
  status_id: string;
  status_name: string;
  status_position: number;
  branch_id: string;
  branch_name: string;
  order_count: number;
}

interface Branch {
  id: string;
  name: string;
}

export default function ReportsHome() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { lang } = useParams();
  const organizationId = useAuthStore(s => s.user?.organizationId);
  
  const [dateRange, setDateRange] = useState<DateRange>('this_week');
  const [branch, setBranch] = useState<string>('all');
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [statusCounts, setStatusCounts] = useState<StatusCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);

  // Fetch branches for dropdown
  useEffect(() => {
    if (!organizationId) return;
    async function fetchBranches() {
      const { data } = await supabase
        .from('branches')
        .select('id, name')
        .eq('organization_id', organizationId);
      if (data) setBranches(data);
    }
    fetchBranches();
  }, [organizationId]);

  const fetchMetrics = async () => {
    if (!organizationId) return;
    
    setFetchError(null);
    setLoading(true);
    
    try {
      const startDate = new Date();
      const endDate = new Date();
      
      if (dateRange === 'this_week') {
        startDate.setDate(startDate.getDate() - 7);
      } else if (dateRange === 'this_month') {
        startDate.setDate(startDate.getDate() - 30);
      } else if (dateRange === 'custom') {
        if (customStartDate) {
          startDate.setTime(new Date(customStartDate).getTime());
        }
        if (customEndDate) {
          endDate.setTime(new Date(customEndDate).getTime());
        }
      }
      
      const { data, error } = await supabase.rpc('reports_metrics', {
        p_organization_id: organizationId,
        p_start_date: startDate.toISOString().split('T')[0],
        p_end_date: endDate.toISOString().split('T')[0],
        p_branch_id: branch === 'all' ? null : branch,
      });
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        const row = data[0];
        if (typeof row.top_equipment === 'string') {
          row.top_equipment = JSON.parse(row.top_equipment);
        }
        setMetrics(row as Metrics);
      } else {
        setMetrics(null);
      }

      // Fetch status breakdown (separate RPC; independent of main metrics failure)
      const { data: statusData, error: statusError } = await supabase.rpc('reports_by_status', {
        p_organization_id: organizationId,
        p_start_date: startDate.toISOString().split('T')[0],
        p_end_date: endDate.toISOString().split('T')[0],
        p_branch_id: branch === 'all' ? null : branch,
      });
      if (statusError) {
        logError('Error fetching status breakdown', { error: getErrorMessage(statusError) });
        setStatusCounts([]);
      } else {
        setStatusCounts((statusData ?? []) as StatusCount[]);
      }
    } catch (err) {
      logError('Error fetching metrics', { error: getErrorMessage(err) });
      setFetchError(getErrorMessage(err) || 'Failed to load reports');
      setMetrics(null);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch and when filters change
  useEffect(() => {
    if (!organizationId) return;
    fetchMetrics();
  }, [dateRange, branch, organizationId]);

  const handleApplyFilters = () => {
    if (dateRange === 'custom') {
      if (!customStartDate || !customEndDate) {
        setFetchError('Please select both start and end dates');
        return;
      }
    }
    fetchMetrics();
  };

  const currentMetrics = metrics ?? { total_orders: 0, revenue: 0, avg_repair_time: 0, top_equipment: [] };
  const navLang = lang || 'en';

  const metricCards = [
    {
      title: t('reports.totalOrders'),
      value: currentMetrics.total_orders.toLocaleString(),
      icon: 'orders',
      onClick: () => navigate(`/${navLang}/admin/orders`),
    },
    {
      title: t('reports.totalRevenue'),
      value: `$${currentMetrics.revenue.toLocaleString()}`,
      icon: 'dashboard',
      onClick: () => navigate(`/${navLang}/admin/reports/detail?view=revenue`),
    },
    {
      title: t('reports.avgRepairTime'),
      value: `${currentMetrics.avg_repair_time} hrs`,
      icon: 'equipment',
      onClick: () => navigate(`/${navLang}/admin/reports/detail?view=time`),
    },
    {
      title: t('reports.topEquipment'),
      value: currentMetrics.top_equipment.length > 0 ? currentMetrics.top_equipment[0].type : '—',
      items: currentMetrics.top_equipment,
      icon: 'users',
      onClick: () => navigate(`/${navLang}/admin/reports/detail?view=equipment`),
    },
  ];

  return (
    <>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-nb-black">{t('reports.title')}</h1>
        <p className="text-nb-text-secondary mt-2">{t('reports.subtitle')}</p>
      </div>

      {fetchError && (
        <div className="mb-4 px-4 py-3 rounded bg-nb-red/10 text-nb-red text-sm">
          {fetchError}
        </div>
      )}

      <CardNB className="p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <SelectNB
              label={t('reports.dateRange')}
              value={dateRange}
              onChange={(e) => {
                setDateRange(e.target.value as DateRange);
                setShowCustomDate(e.target.value === 'custom');
              }}
              className="w-full"
            >
              <option value="this_week">{t('reports.thisWeek')}</option>
              <option value="this_month">{t('reports.thisMonth')}</option>
              <option value="custom">{t('reports.custom')}</option>
            </SelectNB>
          </div>

          {showCustomDate && (
            <>
              <div>
                <label className="font-nb font-medium text-sm text-nb-text-secondary block mb-1">{t('reports.from')}</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="bg-nb-white border-3 border-nb-black px-4 py-2 text-sm font-nb rounded-[2px] focus:border-nb-yellow focus:outline-none"
                />
              </div>
              <div>
                <label className="font-nb font-medium text-sm text-nb-text-secondary block mb-1">{t('reports.to')}</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="bg-nb-white border-3 border-nb-black px-4 py-2 text-sm font-nb rounded-[2px] focus:border-nb-yellow focus:outline-none"
                />
              </div>
            </>
          )}

          <div>
            <SelectNB
              label={t('reports.branch')}
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
              className="w-full"
            >
              <option value="all">{t('reports.allBranches')}</option>
              {branches.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </SelectNB>
          </div>

          <ButtonNB variant="primary" size="sm" onClick={handleApplyFilters}>
            {t('reports.applyFilters')}
          </ButtonNB>
        </div>
      </CardNB>

      {loading ? (
        <p className="text-nb-text-muted">Loading...</p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {metricCards.map((card, idx) => (
              <div
                key={idx}
                onClick={card.onClick}
                className="cursor-pointer"
              >
                <CardNB variant="interactive" className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <IconNB name={card.icon} size={24} />
                  </div>
                  <h3 className="text-sm font-nb font-medium text-nb-text-muted mb-2">{card.title}</h3>
                  {card.items ? (
                    <ul className="flex flex-col gap-1">
                      {card.items.map((item, i) => (
                        <li key={i} className="text-sm text-nb-text-secondary flex justify-between">
                          <span>{item.type}</span>
                          <span className="font-nb font-medium">{item.count}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xl font-bold text-nb-black">{card.value}</p>
                  )}
                  <p className="text-nb-black text-xs mt-4">{t('reports.viewDetails')}</p>
                </CardNB>
              </div>
            ))}
          </div>

          {/* Orders by status — Bug #7 */}
          {statusCounts.length > 0 && (
            <div className="mt-10">
              <h2 className="text-lg font-nb font-bold text-nb-black mb-1">
                {t('reports.byStatus.heading') || 'Orders by status'}
              </h2>
              <p className="text-nb-text-secondary text-sm mb-4">
                {t('reports.byStatus.subtitle') || 'How many orders are currently in each workflow state.'}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {Object.entries(
                  statusCounts.reduce<Record<string, { name: string; position: number; count: number; perBranch: StatusCount[] }>>((acc, row) => {
                    if (!acc[row.status_id]) {
                      acc[row.status_id] = { name: row.status_name, position: row.status_position, count: 0, perBranch: [] };
                    }
                    acc[row.status_id].count += row.order_count;
                    acc[row.status_id].perBranch.push(row);
                    return acc;
                  }, {})
                )
                  .sort(([, a], [, b]) => a.position - b.position)
                  .map(([statusId, info]) => (
                    <CardNB
                      key={statusId}
                      variant="interactive"
                      className="p-4 cursor-pointer"
                      onClick={() => navigate(`/${navLang}/admin/orders?status=${statusId}`)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-nb-text-muted font-nb">
                          #{info.position}
                        </span>
                        <IconNB name="orders" size={16} />
                      </div>
                      <h3 className="text-sm font-nb font-medium text-nb-black mb-2">
                        {info.name}
                      </h3>
                      <p className="text-2xl font-bold text-nb-black">
                        {info.count.toLocaleString()}
                      </p>
                      {info.perBranch.length > 1 && (
                        <p className="text-xs text-nb-text-muted mt-2">
                          {info.perBranch.length} {t('reports.byStatus.branches') || 'branches'}
                        </p>
                      )}
                    </CardNB>
                  ))}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
